import type { Target } from 'src/models/TimerTarget';

import { CaretDownIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';
import * as React from 'react';
import { css, html } from 'react-strict-dom';
import { Stack, Text, TimerCompanyLogo } from 'src/components';
import { Color, Style } from 'src/constants';
import { color } from 'src/constants/color.css';

/** Long enough to scroll, short enough not to swallow the form under it. */
const MAX_SHOWN = 40;

type Props = {
  options: ReadonlyArray<Target>;
  selected: Target | null;
  onSelect: (target: Target) => void;
};

/**
 * What the time is being logged against.  The list is every client, project
 * and service order, plus whatever has been worked on lately, filtered as you
 * type.
 */
export default function TimeLogTargetPicker({
  options,
  selected,
  onSelect,
}: Props) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    window.addEventListener('click', onClickOutside, true);
    return () => window.removeEventListener('click', onClickOutside, true);
  }, [open]);

  const needle = query.trim().toLowerCase();
  const matches = options
    .filter((option) =>
      needle === ''
        ? true
        : `${option.title} ${option.reference ?? ''} ${
            option.company?.name ?? ''
          }`
            .toLowerCase()
            .includes(needle)
    )
    .slice(0, MAX_SHOWN);

  return (
    <Stack gap={0.5}>
      <Text typeface="body.small.regular" color="neutral80">
        What are you logging against?
      </Text>

      <html.div ref={ref} style={Style.positionRelative}>
        <html.div
          style={[Style.row, Style.alignCenter, styles.control]}
          onClick={() => setOpen((v) => !v)}
        >
          {selected ? (
            <>
              <TimerCompanyLogo
                company={selected.company}
                title={selected.title}
              />
              <html.span style={styles.grow}>
                <Text typeface="body.regular" color="neutral100">
                  {selected.title}
                </Text>
              </html.span>
              {selected.reference && (
                <Text typeface="body.small.medium" color="neutral60">
                  {selected.reference}
                </Text>
              )}
            </>
          ) : (
            <>
              <MagnifyingGlassIcon size={18} color={Color.neutral40} />
              <html.span style={styles.grow}>
                <Text typeface="body.regular" color="neutral40">
                  Choose a client, project, service order or ticket
                </Text>
              </html.span>
            </>
          )}
          <CaretDownIcon size={14} color={Color.neutral60} />
        </html.div>

        {open && (
          <html.div style={styles.panel}>
            <html.input
              style={styles.search}
              value={query}
              placeholder="Search"
              autoFocus
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setQuery(e.target.value)
              }
            />
            <html.div style={styles.list}>
              {matches.length === 0 && (
                <html.div style={styles.empty}>
                  <Text typeface="body.small.regular" color="neutral60">
                    Nothing matches that.
                  </Text>
                </html.div>
              )}
              {matches.map((option) => (
                <html.div
                  key={option.iri}
                  style={[Style.row, Style.alignCenter, styles.option]}
                  onClick={() => {
                    onSelect(option);
                    setQuery('');
                    setOpen(false);
                  }}
                >
                  <TimerCompanyLogo
                    company={option.company}
                    title={option.title}
                  />
                  <html.span style={styles.grow}>
                    <Text typeface="body.small.medium" color="neutral100">
                      {option.title}
                    </Text>
                  </html.span>
                  <Text typeface="caption.medium" color="neutral60">
                    {option.reference ?? option.company?.name ?? ''}
                  </Text>
                </html.div>
              ))}
            </html.div>
          </html.div>
        )}
      </html.div>
    </Stack>
  );
}

const styles = css.create({
  control: {
    gap: 8,
    padding: 12,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    borderRadius: 8,
    backgroundColor: color.neutral0,
    cursor: 'pointer',
  },
  grow: {
    flexGrow: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  panel: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: 4,
    zIndex: 100,
    backgroundColor: color.neutral0,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    borderRadius: 8,
    boxShadow: '0px 4px 22px rgba(0,0,0,0.1)',
    overflow: 'hidden',
  },
  search: {
    width: '100%',
    padding: 10,
    borderWidth: 0,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: color.neutral20,
    fontSize: 14,
    fontFamily: 'Inter, sans-serif',
    lineHeight: 1.5,
    color: color.neutral100,
    outline: 'none',
  },
  list: {
    maxHeight: 260,
    overflowY: 'auto',
  },
  option: {
    gap: 8,
    paddingInline: 12,
    paddingBlock: 10,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: color.neutral10,
    },
  },
  empty: {
    paddingInline: 12,
    paddingBlock: 12,
  },
});
